import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {QlBuilderModel} from "../../../../shared/ql/builder/ql-builder-model";
import {QlBuilderTreeOps} from "../../../../shared/ql/component/ql-builder/logic/ql-builder-tree-ops";
import {
    UiQlWidgetCategoryMappingDto,
    UiQlWidgetChartMappingDto,
    UiQlWidgetMapping,
    UiQlWidgetType,
} from "../../../model/dto/api/ui-ql-widget-mapping.dto";
import {UI_QL_WIDGET_TYPE_REGISTRY} from "../../../logic/ui-ql-widget-type-registry";
import {UiQlWidgetMappingDefaults} from "../../../logic/ui-ql-widget-mapping-defaults";
import {UiQlWidgetTypeOption} from "../../../interface/ui-ql-widget-type-option.interface";
import {UiQlOption} from "../../../interface/ui-ql-option.interface";
import {MetaService} from "../../../../shared/meta/service/meta.service";
import {MetaDataEntityDto} from "../../../../shared/meta/dto/meta-data-entity.dto";
import {QlBuilderKeyLabels} from "../../../../shared/ql/component/ql-builder/logic/ql-builder-key-labels";
import {QlBuilderKeyOption} from "../../../../shared/ql/component/ql-builder/interface/ql-builder-key-option.interface";

/**
 * Widget-type area of the QL workbench: "Tabelle | Liste | Diagramm | Kategorie" plus the
 * type-dependent mapping from `UI_QL_WIDGET_TYPE_REGISTRY` (required keys, value checks). The
 * selectable result keys are DERIVED FROM THE MODEL (grouping columns / aggregates) — no probe run.
 * Diagramm and Kategorie need the grouping mode: a hint plus "Gruppierung aktivieren" is shown.
 *
 * Emits only (no state access): the page forwards to `UiQlWidgetWorkbenchStateService`.
 *
 * Declared in `UiQlWidgetBuilderModule` (NgModule, fe_core_standards) - its only host is
 * `UiQlWidgetWorkbenchPage` of the same module.
 */
@Component({
    selector: 'ui-ql-widget-type-editor',
    templateUrl: './ui-ql-widget-type-editor.component.html',
    styleUrls: ['./ui-ql-widget-type-editor.component.scss'],
    standalone: false,
})
export class UiQlWidgetTypeEditorComponent implements OnChanges, OnDestroy {

    static readonly TEXT_GROUP_BY_REQUIRED = 'Diagramm und Kategorie brauchen eine gruppierte Abfrage.';

    @Input() model: QlBuilderModel | null = null;
    @Input() widgetType: UiQlWidgetType = 'table';
    @Input() mapping: UiQlWidgetMapping = {};

    @Output() widgetTypeChange = new EventEmitter<UiQlWidgetType>();
    @Output() mappingChange = new EventEmitter<UiQlWidgetMapping>();
    @Output() modelChange = new EventEmitter<QlBuilderModel>();

    readonly textGroupByRequired = UiQlWidgetTypeEditorComponent.TEXT_GROUP_BY_REQUIRED;

    readonly chartTypeOptions: UiQlOption<UiQlWidgetChartMappingDto['chartType']>[] = [
        {label: 'Balken', value: 'bar'},
        {label: 'Linie', value: 'line'},
        {label: 'Kreis', value: 'pie'},
        {label: 'Fläche', value: 'area'},
    ];

    readonly functionOptions: UiQlOption<UiQlWidgetChartMappingDto['chartAggregateFunction']>[] = [
        {label: 'Anzahl', value: 'count'},
        {label: 'Summe', value: 'sum'},
        {label: 'Durchschnitt', value: 'avg'},
        {label: 'Minimum', value: 'min'},
        {label: 'Maximum', value: 'max'},
    ];

    readonly displayModeOptions: UiQlOption<string>[] = [
        {label: 'Donut', value: 'doughnut'},
        {label: 'Einfach', value: 'simple'},
        {label: 'Fortschritt', value: 'progress'},
        {label: 'Balken', value: 'bar'},
        {label: 'Linie', value: 'line'},
        {label: 'Polar', value: 'polarArea'},
        {label: 'Radar', value: 'radar'},
    ];

    typeOptions: UiQlWidgetTypeOption[] = [];
    /** Result keys: German label from the metadata/model, the key itself as secondary text. */
    groupKeyOptions: QlBuilderKeyOption[] = [];
    aggregateKeyOptions: QlBuilderKeyOption[] = [];

    /** The selected type needs grouping but the model is not grouped. */
    needsGroupBy: boolean = false;

    /** Why "Gruppierung aktivieren" is not possible — `null` = possible. */
    enableGroupByBlockedReason: string | null = null;

    /** German mapping problems (missing keys, unknown result keys, invalid values). */
    mappingIssues: string[] = [];

    /** Why Diagramm/Kategorie cannot be chosen right now - shown as text below the selection (QA #10), `null` = all selectable. */
    disabledTypesReason: string | null = null;

    /**
     * Join fields that act as ADDITIONAL grouping keys next to the mapped category/x-axis (QA #5): every
     * combination becomes its own slice/bar, which is rarely what the mapping means. Empty in row mode.
     */
    extraGroupKeys: string[] = [];

    /** Metadata of the start entity — only for the German labels of the mapping keys (all on the root). */
    private rootMeta: MetaDataEntityDto | null = null;
    private loadedRootAlias: string | null = null;
    private metaSubscription: Subscription | null = null;

    constructor(private readonly metaService: MetaService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        const alias = this.model?.root?.entityName ?? null;
        if (alias !== this.loadedRootAlias) {
            this.loadRootMeta(alias);
        }
        this.recompute();
    }

    ngOnDestroy(): void {
        this.metaSubscription?.unsubscribe();
    }

    get chartMapping(): UiQlWidgetChartMappingDto {
        return this.mapping as UiQlWidgetChartMappingDto;
    }

    get categoryMapping(): UiQlWidgetCategoryMappingDto {
        return this.mapping as UiQlWidgetCategoryMappingDto;
    }

    selectType(widgetType: UiQlWidgetType | null): void {
        if (!widgetType || widgetType === this.widgetType) {
            return;
        }
        const option = this.typeOptions.find(o => o.value === widgetType);
        if (option?.disabled) {
            return;
        }
        this.widgetTypeChange.emit(widgetType);
        this.mappingChange.emit(UiQlWidgetMappingDefaults.reconcile(widgetType, this.model, this.mapping));
    }

    /** "Gruppierung aktivieren" — switches the model into grouping mode and fills the mapping. */
    enableGroupBy(): void {
        if (!this.model || this.enableGroupByBlockedReason) {
            return;
        }
        const grouped = QlBuilderTreeOps.enableGroupBy(this.model);
        this.modelChange.emit(grouped);
        this.mappingChange.emit(UiQlWidgetMappingDefaults.reconcile(this.widgetType, grouped, this.mapping));
    }

    /** "Join-Felder entfernen" (QA #5): drops the join fields, so only the root's grouping keys remain. */
    clearJoinFields(): void {
        if (this.model) {
            this.modelChange.emit(QlBuilderTreeOps.clearJoinFields(this.model));
        }
    }

    /** "Automatisch zuordnen" — fill missing/stale mapping keys from the current result keys. */
    autoMap(): void {
        this.mappingChange.emit(UiQlWidgetMappingDefaults.reconcile(this.widgetType, this.model, this.mapping));
    }

    setMappingValue(key: string, value: unknown): void {
        const next: { [key: string]: unknown } = {...(this.mapping ?? {})};
        if (value === null || value === undefined || value === '') {
            delete next[key];
        } else {
            next[key] = value;
        }
        this.mappingChange.emit(next as unknown as UiQlWidgetMapping);
    }

    private recompute(): void {

        const blockedByLeaves = QlBuilderTreeOps.hasExtendedOrSideCar(this.model)
            ? 'Nicht möglich: erweiterte Felder oder externe Zusatzfelder lassen sich nicht gruppieren.'
            : null;

        this.typeOptions = [
            {label: 'Tabelle', value: 'table', icon: 'fa-regular fa-table', disabled: false, tooltip: 'Zeilen und Spalten der gewählten Felder'},
            {label: 'Liste', value: 'list', icon: 'fa-regular fa-list', disabled: false, tooltip: 'Kartenliste der gewählten Felder'},
            {
                label: 'Diagramm', value: 'chart', icon: 'fa-regular fa-chart-column',
                disabled: !!blockedByLeaves, tooltip: blockedByLeaves ?? 'Balken, Linie, Kreis oder Fläche über gruppierte Werte',
            },
            {
                label: 'Kategorie', value: 'category', icon: 'fa-regular fa-chart-pie',
                disabled: !!blockedByLeaves, tooltip: blockedByLeaves ?? 'Anzahl je Gruppe, z. B. Tickets je Status',
            },
        ];

        this.disabledTypesReason = blockedByLeaves ? `Diagramm und Kategorie nicht wählbar: ${blockedByLeaves}` : null;

        const entry = UI_QL_WIDGET_TYPE_REGISTRY[this.widgetType] ?? UI_QL_WIDGET_TYPE_REGISTRY.table;
        this.needsGroupBy = entry.requiresGroupBy && !this.model?.groupBy;
        this.extraGroupKeys = entry.requiresGroupBy && this.model?.groupBy ? QlBuilderTreeOps.joinFieldKeys(this.model) : [];
        this.enableGroupByBlockedReason = !this.model
            ? 'Zuerst eine Start-Entität wählen.'
            : QlBuilderTreeOps.enableGroupByBlockedReason(this.model);

        const keys = UiQlWidgetMappingDefaults.keys(this.model);
        const metas = new Map<string, MetaDataEntityDto>();
        if (this.rootMeta && this.loadedRootAlias) {
            metas.set(this.loadedRootAlias, this.rootMeta);
        }
        const labels = QlBuilderKeyLabels.labels(this.model, metas);
        this.groupKeyOptions = keys.groupKeys.map(k => ({label: QlBuilderKeyLabels.labelOf(labels, k), value: k}));
        this.aggregateKeyOptions = keys.aggregateKeys.map(k => ({label: QlBuilderKeyLabels.labelOf(labels, k), value: k}));

        this.mappingIssues = this.computeMappingIssues(entry.requiredMappingKeys, [...keys.groupKeys, ...keys.aggregateKeys]);
    }

    /** Cached by `MetaService`; a failed load only leaves the raw keys as labels. */
    private loadRootMeta(alias: string | null): void {
        this.metaSubscription?.unsubscribe();
        this.loadedRootAlias = alias;
        this.rootMeta = null;
        if (!alias) {
            return;
        }
        this.metaSubscription = this.metaService.getByAlias(alias, true, true, true, true, true).subscribe({
            next: meta => {
                this.rootMeta = meta;
                this.recompute();
            },
            error: () => { /* labels fall back to the keys */ },
        });
    }

    private computeMappingIssues(requiredKeys: string[], resultKeys: string[]): string[] {

        const entry = UI_QL_WIDGET_TYPE_REGISTRY[this.widgetType];
        if (!entry || !entry.requiredMappingKeys.length || this.needsGroupBy) {
            return [];
        }

        const values = (this.mapping ?? {}) as { [key: string]: unknown };
        const missing = requiredKeys.filter(key => {
            const value = values[key];
            return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
        });
        if (missing.length) {
            return missing.map(key => `Zuordnung „${UiQlWidgetTypeEditorComponent.keyLabel(key)}“ fehlt.`);
        }
        return entry.validateMapping(this.mapping, resultKeys).map(issue => issue.message);
    }

    static keyLabel(key: string): string {
        switch (key) {
            case 'chartType': return 'Diagrammart';
            case 'chartField1st': return 'X-Achse';
            case 'chartField2nd': return 'Serie';
            case 'chartFieldAggregate': return 'Wert';
            case 'chartAggregateFunction': return 'Aggregat';
            case 'fieldCategory': return 'Kategorie';
            case 'fieldCategoryCount': return 'Anzahl';
            case 'displayMode': return 'Darstellung';
            default: return key;
        }
    }
}
